// src/auth/userHelpers.js
//
// Helpers shared by LoginPage and SignupPage.
//
// ensureUserDoc: creates users/{uid} if missing, updates lastLogin always.
//   The role field is NOT managed here — LoginPage writes it from
//   configuration/main.users during phase 1. Ticket 04 moves role to the
//   memberships collection; ticket 10 removes the legacy users/{uid}.role
//   field entirely.
//
// tryLegacyPasswordMigration: handles the one-shot password migration for
//   users still holding the dummy password from the pre-ticket-02 flow. On
//   the user's first post-cutover login, whatever password they type becomes
//   their real password. The DUMMY_PASSWORD fallback disappears for a user
//   once legacyPasswordMigrated is true on their users/{uid} doc.
//
//   Security note: we don't actually gate the dummy fallback on the flag.
//   After updatePassword() lands, the dummy is no longer a valid credential
//   (Firebase Auth only stores one password per user), so attempting the
//   fallback on a migrated user just fails naturally. The flag is a marker
//   for tooling and a signal to ticket 10 about which users have rotated.

import {
  signInWithEmailAndPassword,
  updatePassword,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '../firebase';

export const DUMMY_PASSWORD = 'Password123!';

/**
 * Creates users/{uid} if missing, or updates lastLogin on the existing doc.
 * Extra fields from `overrides` are merged (used for legacyPasswordMigrated,
 * role during phase 1, displayName on signup, etc.).
 */
export async function ensureUserDoc(uid, { email, overrides = {} } = {}) {
  const ref = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  const base = {
    email: (email || '').toLowerCase(),
    lastLogin: serverTimestamp(),
    ...overrides,
  };
  if (!snap.exists()) {
    await setDoc(ref, {
      ...base,
      createdAt: serverTimestamp(),
    });
  } else {
    await setDoc(ref, base, { merge: true });
  }
}

/**
 * Attempts the one-shot dummy-password fallback for a legacy user whose
 * real login attempt failed with auth/wrong-password. On success:
 *
 *   1. updatePassword() swaps the stored password to the entered value.
 *   2. users/{uid}.legacyPasswordMigrated is set to true.
 *
 * Returns the userCredential on success, or null if the fallback didn't
 * apply (dummy also failed — i.e. the user is already migrated or the
 * typed password is just wrong).
 */
export async function tryLegacyPasswordMigration(email, enteredPassword) {
  let credential;
  try {
    credential = await signInWithEmailAndPassword(auth, email, DUMMY_PASSWORD);
  } catch (err) {
    // Dummy fallback didn't work. Either the user is already migrated, or
    // the user genuinely doesn't exist, or the password was rotated by
    // something outside this flow. Either way: not a legacy migration.
    return null;
  }

  // Dummy worked — this is a legacy user whose first post-cutover login
  // we've just caught. Promote whatever they typed to the real password.
  try {
    await updatePassword(credential.user, enteredPassword);
  } catch (updateErr) {
    // updatePassword can fail if entered password is too weak. Surface to
    // the caller so they can show a clean error. The user is still signed
    // in on the dummy password; the caller should sign them out.
    const migrationErr = new Error(
      `Your new password was rejected: ${updateErr.message}. Try a stronger password.`,
    );
    migrationErr.code = 'legacy-migration/update-password-failed';
    migrationErr.cause = updateErr;
    throw migrationErr;
  }

  await setDoc(
    doc(db, 'users', credential.user.uid),
    {
      legacyPasswordMigrated: true,
      lastLogin: serverTimestamp(),
    },
    { merge: true },
  );

  return credential;
}
